import React, { useState, useEffect } from 'react';
import { 
  Gauge, Zap, Shield, Info, Loader, 
  Car, Fuel, Settings, Wind, AlertCircle, BarChart,
  Wrench, Package, Palette, AlertTriangle, RefreshCw,
  Database, Cloud, Star
} from 'lucide-react';
import VehicleSpecificationsDisplay from './VehicleSpecificationsDisplay';
import MileageUpdateComponent from './MileageUpdateComponent';
import api from '../services/api';

const VehicleDetailsTab = ({ vehicle }) => {
  const [specs, setSpecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [dataSource, setDataSource] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadSpecifications();
  }, [vehicle]);

  const loadSpecifications = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      setRefreshing(forceRefresh);

      // Use the configured API service with proper authentication
      const response = await api.get(
        `/vehicle-profiles/${vehicle.id}/specifications${forceRefresh ? '?force_refresh=true' : ''}`
      );

      const result = response.data;
      
      if (result.data) {
        setSpecs(result.data);
        setDataSource({
          source: result.source,
          fetchedAt: result.fetched_at
        });
      } else {
        setError('Failed to load specifications');
      }
    } catch (err) {
      console.error('Error loading specifications:', err);
      setError('Failed to load vehicle specifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadSpecifications(true);
  };

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-accent" />
        <span className="ml-2 text-ink-muted">Loading specifications...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-tint rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-danger mx-auto mb-3" />
        <p className="text-danger">{error}</p>
        <button onClick={() => loadSpecifications()} className="mt-2 text-danger hover:text-danger/80">
          Try Again
        </button>
      </div>
    );
  }

  const sections = [
    { id: 'overview', label: 'Vehicle Overview', icon: Car },
    { id: 'premium', label: 'Premium Specifications', icon: Star },
    { id: 'recalls', label: 'Recalls & Safety', icon: AlertTriangle }
  ];

  const isFromCache = dataSource?.source === 'cache' || dataSource?.source === 'redis_cache';

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Vehicle Specifications</h2>
          <div className="flex items-center gap-2">
            {dataSource && (
              <CacheIndicator isFromCache={isFromCache} fetchedAt={dataSource.fetchedAt} />
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`p-2 text-ink-muted hover:text-ink-soft hover:bg-surface-muted rounded-lg transition-colors ${
                refreshing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Refresh specifications"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        <div className="flex space-x-2 overflow-x-auto">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeSection === section.id
                  ? 'bg-accent text-white'
                  : 'bg-surface-muted text-ink-soft hover:bg-surface-muted'
              }`}
            >
              <section.icon className="w-4 h-4 mr-2" />
              {section.label}
              {section.id === 'recalls' && specs?.recalls?.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-small bg-danger text-white rounded-full">
                  {specs.recalls.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* Vehicle Basic Info */}
          {specs?.basic && (
            <div className="bg-info-tint rounded-lg p-6 border border-info-border">
              <h3 className="text-lg font-semibold mb-3 text-info">
                {specs.basic.year} {specs.basic.make} {specs.basic.model}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-info">Trim</p>
                  <p className="font-medium text-info">{specs.basic.trim}</p>
                </div>
                <div>
                  <p className="text-info">Doors</p>
                  <p className="font-medium text-info">{specs.basic.doors} doors</p>
                </div>
                <div>
                  <p className="text-info">MSRP</p>
                  <p className="font-medium text-info">{specs.market_value?.msrp || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-info">Seating</p>
                  <p className="font-medium text-info">{specs.seating?.standard_seating || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Mileage Update Component */}
          <MileageUpdateComponent
            vehicleId={vehicle?.id}
            currentMileage={vehicle?.mileage || 0}
            onMileageUpdate={(newMileage) => {
              // Update the vehicle object with new mileage
              vehicle.mileage = newMileage;
              // Optionally refresh specifications with new mileage
              loadSpecifications();
            }}
            showUnitToggle={true}
          />

          {/* Additional Vehicle Details */}
          <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
            <h3 className="text-lg font-semibold mb-4 text-ink-soft">Vehicle Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-ink-soft mb-2">Engine & Performance</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Engine:</span>
                    <span className="font-medium">{vehicle.engine || specs?.engine?.type || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Body Style:</span>
                    <span className="font-medium">{vehicle.body_style || specs?.basic?.body_style || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Transmission:</span>
                    <span className="font-medium">{specs?.transmission?.type || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-ink-soft mb-2">Ownership Info</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Purchase Date:</span>
                    <span className="font-medium">
                      {vehicle.purchase_date ? new Date(vehicle.purchase_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Usage Type:</span>
                    <span className="font-medium capitalize">{vehicle.usage_type || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Annual Miles:</span>
                    <span className="font-medium">{vehicle.annual_miles ? `${vehicle.annual_miles.toLocaleString()} miles` : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Premium Data Section */}
      {activeSection === 'premium' && (
        <div className="space-y-6">
          <div className="bg-info-tint rounded-lg p-6 border border-info-border">
            <h3 className="text-lg font-semibold mb-3 text-info flex items-center">
              <Star className="w-5 h-5 mr-2" />
              Comprehensive Premium Data
            </h3>
            <p className="text-info mb-4">
              Detailed specifications and data from premium VIN decoder
            </p>
          </div>
          
          <VehicleSpecificationsDisplay vehicleProfileId={vehicle.id} />
        </div>
      )}

      {/* Recalls Section */}
      {activeSection === 'recalls' && (
        <div className="bg-info-tint rounded-lg p-8 text-center border border-info-border">
          <div className="w-16 h-16 bg-info-tint rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-accent" />
          </div>
          <h3 className="text-section-title font-semibold text-ink-soft mb-2">Recalls & Safety</h3>
          <p className="text-ink-muted mb-4">Coming Soon</p>
          <p className="text-sm text-ink-muted max-w-md mx-auto">
            We're working on integrating recall and safety data for your vehicle. 
            This feature will automatically check for active recalls and safety notices.
          </p>
        </div>
      )}
    </div>
  );
};

// Cache indicator component
function CacheIndicator({ isFromCache, fetchedAt }) {
  if (!isFromCache) return null;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  
  return (
    <div className="flex items-center gap-1 text-small text-ink-muted">
      <Database className="w-3 h-3" />
      <span>Cached {formatDate(fetchedAt)}</span>
    </div>
  );
}

export default VehicleDetailsTab;